import { datadogRum } from '@datadog/browser-rum';
import { axios } from '~/clients';
import { SUGGESTIONSV3 } from '~/constants/endpoints';
import type { Suggestion } from '~/contracts';
import { logError, toError } from '~/utilities/errorHandlers';

type SuggestionsResponse = {
  suggestions: Suggestion[];
};

export async function fetchSuggestionsApi(
  query: string,
  controller: AbortController
): Promise<Suggestion[] | undefined> {
  try {
    const {
      data: { suggestions }
    } = await axios.get<SuggestionsResponse>(
      `${SUGGESTIONSV3}?query=${query}`,
      {
        signal: controller.signal
      }
    );

    return suggestions;
  } catch (e) {
    logError(toError(e), { location: 'Get Suggestions' }, { controller });

    return undefined;
  }
}

const savedSuggestionKey = 'CVSavedSuggestions';

function removeLegacySearches(savedSearches: Suggestion[]): Suggestion[] {
  const filteredSearches = savedSearches.filter(
    currentItem => !currentItem.hasOwnProperty('legacyFilters')
  );

  if (savedSearches.length !== filteredSearches.length) {
    datadogRum.addAction('Homepage legacyFilters removed');
  }

  localStorage.setItem(savedSuggestionKey, JSON.stringify(filteredSearches));

  return filteredSearches;
}

export function getFromLocalStorage(): Suggestion[] {
  const saved = localStorage.getItem(savedSuggestionKey);

  return saved
    ? removeLegacySearches(
        JSON.parse(saved).map((suggestion: Suggestion) => ({
          ...suggestion,
          isSavedSuggestion: true
        }))
      )
    : [];
}

export function saveToLocalStorage(savedSuggestion: Suggestion[]) {
  localStorage.setItem(savedSuggestionKey, JSON.stringify(savedSuggestion));
}

export function saveSuggestion(suggestion: Suggestion) {
  const existing = getFromLocalStorage();
  if (existing.find(sug => sug.text === suggestion.text)) {
    return existing;
  }

  return [{ isSavedSuggestion: true, ...suggestion }, ...existing].slice(0, 10);
}

export function removeSuggestion(suggestion: Suggestion) {
  const existing = getFromLocalStorage();

  return existing.filter(sug => sug.text !== suggestion.text);
}
